import type {
    CSSProperties,
    ElementType,
    FC,
    HTMLAttributes,
    ReactNode
} from 'react'

import { Fragment } from 'react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'

/** Gap between letters: 45ms like the hero, shorter on long text so a line lands in about 1.5 seconds. */
export const dotStep = (letters: number): string => `${Math.max(10, Math.min(45, Math.round(1400 / Math.max(letters, 1))))}ms`

interface DotCharsProps {
    text: string
    /** Index of the first letter in the whole line, so the wave keeps running across words. */
    start?: number
}

/**
 * The letters of one word, each resolving out of slate dots like the hero
 * headline. Needs an ancestor with .dot-reveal (plays once it has
 * data-inview), .dot-load or .dot-headline to animate.
 */
export const DotChars: FC<DotCharsProps> = ({ text, start = 0 }): ReactNode => {
    return (
        <>
            {Array.from(text).map((char, index) => (
                <span key={index} className='dot-char' data-char={char} style={{ '--i': start + index } as CSSProperties}>
                    <span className='dot-glyph'>
                        {char}
                    </span>
                    <span className='dot-cluster' />
                </span>
            ))}
        </>
    )
}

interface PixelTextProps extends HTMLAttributes<HTMLElement> {
    as?: ElementType
    text: string
    /** 'view' plays when the text scrolls into view, 'load' plays on first paint. */
    mode?: 'load' | 'view'
    /** Milliseconds before the first letter starts. */
    delay?: number
}

/**
 * Text whose letters resolve out of slate dots from left to right, the same
 * reveal as the hero headline, played once when it scrolls into view. Only
 * opacity and backgrounds change, so the text keeps its exact font, colour,
 * size and position. Screen readers get the plain sentence from a visually
 * hidden copy.
 */
const PixelText: FC<PixelTextProps> = ({
    as: Tag = 'h2',
    text,
    mode = 'view',
    delay = 0,
    className,
    style,
    ...rest
}): ReactNode => {
    const ref = useInView<HTMLElement>({ rootMargin: '0px 0px -10% 0px' })
    const words = text.split(' ')
    const letters = text.replace(/ /g, '').length
    let index = 0

    return (
        <Tag
            ref={mode === 'view' ? ref : undefined}
            className={cn(mode === 'load' ? 'dot-load' : 'dot-reveal', className)}
            style={{ ...style, '--dot-delay': `${delay}ms`, '--dot-step': dotStep(letters) } as CSSProperties}
            {...rest}
        >
            <span className='sr-only'>
                {text}
            </span>

            <span aria-hidden='true'>
                {words.map((word, wordIndex) => {
                    const start = index
                    index += Array.from(word).length

                    return (
                        <Fragment key={`${word}-${wordIndex}`}>
                            <span className='inline-block whitespace-nowrap'>
                                <DotChars text={word} start={start} />
                            </span>
                            {wordIndex < words.length - 1 && ' '}
                        </Fragment>
                    )
                })}
            </span>
        </Tag>
    )
}

export default PixelText
